/**
 * V70G6E — 엔글 오목 상세 경기 규칙 모달.
 *   - 게임센터 [경기 규칙] 버튼 + 대국 중에도 열람 가능.
 *   - PC/모바일 반응형.
 *   - 서버 구현과 일치하는 설명만 수록 (실제 코드 반영).
 *   - V70-E — "6. 알려진 한계" 항목은 사용자 지시로 화면에서 제거 (게임 로직·서버 판정 변경 없음).
 *   - V70-M — theme 'dark' (게임센터 다크 테마). 기본 'light' 는 기존 표시 그대로 (대국 화면).
 *     규칙 문구·보드 그림(목판·돌·금수 표시)은 변경 없음.
 */
import React from 'react';
import { X, BookOpen } from 'lucide-react';
import { DARK } from '../shared/tournamentDarkTheme';

interface OmokRulesModalProps {
  onClose: () => void;
  theme?: 'light' | 'dark';
}

const LIGHT_PAL = {
  overlay: 'rgba(0,0,0,0.5)', box: '#fff', boxBorder: 'none', shadow: '0 20px 40px rgba(0,0,0,0.2)',
  headerBg: '#f0fdf4', line: '#e5e7eb', accent: '#065f46', body: '#1f2937',
  navBg: '#f8fafc', navText: '#475569', muted: '#64748b', footerBg: '#f8fafc', btnBg: '#065f46',
  sectionLine: '#d1fae5', exBg: '#fef2f2', exBorder: '#fecaca', exTitle: '#991b1b',
};
// 오목 고유 초록 포인트 유지, 바탕은 게임대회 다크 토큰
const DARK_PAL: typeof LIGHT_PAL = {
  overlay: 'rgba(0, 0, 0, 0.65)', box: DARK.card, boxBorder: `1px solid ${DARK.border}`, shadow: 'none',
  headerBg: DARK.surface, line: DARK.border, accent: '#6EE7B7', body: DARK.text,
  navBg: DARK.row, navText: DARK.textMuted, muted: DARK.textMuted, footerBg: DARK.surface, btnBg: '#047857',
  sectionLine: 'rgba(16, 185, 129, 0.35)', exBg: '#2A1418', exBorder: 'rgba(239, 68, 68, 0.45)', exTitle: '#FCA5A5',
};
const PalContext = React.createContext(LIGHT_PAL);

export function OmokRulesModal({ onClose, theme = 'light' }: OmokRulesModalProps) {
  const pal = theme === 'dark' ? DARK_PAL : LIGHT_PAL;
  return (
    <PalContext.Provider value={pal}>
    <div
      style={{
        position: 'fixed', inset: 0, background: pal.overlay,
        zIndex: 1500, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
      }}
      onClick={onClose}
      data-testid="omok-rules-modal"
    >
      <div
        style={{
          background: pal.box, border: pal.boxBorder, borderRadius: 12, maxWidth: 720, width: '100%',
          maxHeight: '90vh', overflow: 'auto',
          boxShadow: pal.shadow,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <header style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: `1px solid ${pal.line}`,
          background: pal.headerBg, borderRadius: '12px 12px 0 0',
          position: 'sticky', top: 0, zIndex: 1,
        }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: pal.accent, display: 'flex', alignItems: 'center', gap: 8 }}>
            <BookOpen size={20} /> 엔글 오목 — 경기 규칙
          </h2>
          <button
            style={{ background: 'transparent', border: 0, cursor: 'pointer', color: pal.accent, padding: 4 }}
            onClick={onClose}
            aria-label="닫기"
          ><X size={22} /></button>
        </header>

        <div style={{ padding: '20px 24px', fontSize: 14, lineHeight: 1.7, color: pal.body }}>
          {/* 목차 */}
          <nav style={{
            padding: 12, background: pal.navBg, borderRadius: 8, marginBottom: 20,
            fontSize: 13, color: pal.navText,
          }}>
            <strong>목차</strong>
            <ol style={{ margin: '6px 0 0', paddingLeft: 20 }}>
              <li><a href="#basic" style={{ ...navLinkStyle, color: pal.accent }}>기본 규칙</a></li>
              <li><a href="#win" style={{ ...navLinkStyle, color: pal.accent }}>승리 조건</a></li>
              <li><a href="#forbid" style={{ ...navLinkStyle, color: pal.accent }}>흑돌 금수</a></li>
              <li><a href="#flow" style={{ ...navLinkStyle, color: pal.accent }}>경기 진행</a></li>
              <li><a href="#admin" style={{ ...navLinkStyle, color: pal.accent }}>대회 운영</a></li>
            </ol>
          </nav>

          {/* 1. 기본 규칙 */}
          <Section id="basic" title="1. 기본 규칙">
            <ul style={bulletListStyle}>
              <li>2인 대전 (방 코드 공유 방식)</li>
              <li><strong>15 × 15 오목판</strong></li>
              <li>흑돌·백돌 진영은 두 번째 플레이어 입장 시 <strong>서버가 랜덤 결정</strong> (방장이 반드시 흑 아님)</li>
              <li><strong>흑돌이 선공</strong></li>
              <li>자신의 차례에 빈 교차점에 돌을 하나씩 착수</li>
              <li>착수한 돌은 이동하거나 제거할 수 없음</li>
              <li>가로·세로·대각선으로 돌을 연결하여 승리</li>
            </ul>
            <MiniBoardSample caption="15x15 오목판 (예시)" />
          </Section>

          {/* 2. 승리 조건 */}
          <Section id="win" title="2. 승리 조건">
            <ul style={bulletListStyle}>
              <li><strong>흑돌: 정확히 5개 연속</strong> — 장목(6개 이상 연속)은 흑돌에게 승리로 인정되지 않음 (금수)</li>
              <li><strong>백돌: 5개 이상 연속</strong> — 백돌은 장목으로도 승리</li>
              <li>승리 조건 충족 시 즉시 대국 종료</li>
              <li>흑돌이 한 수로 <strong>정확히 5목</strong>을 완성하면, 그 수가 다른 금수 조건(33, 44)에 해당해도 승리로 인정 (공식 RIF 룰 "5목 우선")</li>
              <li>단, 흑돌이 5목과 <strong>장목을 동시에 생성</strong>하는 경우는 공식 렌주룰에 따라 서버가 정확히 판정</li>
            </ul>
          </Section>

          {/* 3. 흑돌 금수 */}
          <Section id="forbid" title="3. 흑돌 금수">
            <p style={{ margin: '0 0 10px', color: pal.muted, fontSize: 13 }}>
              흑돌(선공)에게는 다음 수를 두면 패배가 되는 금수 규칙이 있습니다. 백돌에는 금수 제한이 없습니다.
            </p>
            <ul style={bulletListStyle}>
              <li><strong>33 금수 (삼삼)</strong>: 한 번의 착수로 <strong>유효한 열린 삼</strong>을 <strong>2개 이상</strong> 생성하는 경우</li>
              <li><strong>44 금수 (사사)</strong>: 한 번의 착수로 <strong>사</strong>를 <strong>2개 이상</strong> 생성 (열린 사·단힌 사·점프 사 모두 카운트)</li>
              <li><strong>장목 금수</strong>: 돌이 <strong>6개 이상 연속</strong>되는 모양을 만드는 경우</li>
            </ul>
            <ForbidExamples />
            <p style={{ margin: '12px 0 0', color: pal.muted, fontSize: 13 }}>
              <strong>중요</strong>: 단순 모양만으로 금수를 판단하지 않습니다. 활삼은 "다음 수로 열린 사로 발전 가능한 삼" 이고,
              그 발전 수가 금수(장목·사사)이면 활삼으로 간주되지 않습니다 (공식 RIF 재귀 정의).
              금수 착수는 <strong>서버에서 자동으로 차단</strong>되며 사유가 안내됩니다.
            </p>
          </Section>

          {/* 4. 경기 진행 */}
          <Section id="flow" title="4. 경기 진행">
            <ul style={bulletListStyle}>
              <li>서버에서 턴과 승패 판정 — 클라이언트가 임의로 결과를 바꿀 수 없습니다</li>
              <li>이미 돌이 있는 위치에는 착수 불가</li>
              <li><strong>기권</strong>: 자신이 기권하면 상대방 승리</li>
              <li><strong>연결 끊김</strong>: 네트워크가 끊겼다는 이유만으로 자동 패배 처리하지 않습니다. 재접속하면 진행 중인 경기로 복귀됩니다.</li>
              <li>보드가 가득 찼는데 승자가 없으면 <strong>무승부</strong></li>
              <li>대국 종료 후 추가 착수 불가</li>
            </ul>
          </Section>

          {/* 5. 대회 운영 */}
          <Section id="admin" title="5. 대회 운영">
            <ul style={bulletListStyle}>
              <li>대진표 및 결과 자동 연동 <strong>없음</strong></li>
              <li>승자는 <strong>경기 결과를 운영자에게 직접 전달</strong>해야 합니다</li>
              <li>대국 종료 후 결과 화면이 유지되며, 참가자는 결과를 캡처하거나 기록할 수 있습니다</li>
            </ul>
          </Section>
        </div>

        <footer style={{
          padding: 12, borderTop: `1px solid ${pal.line}`, textAlign: 'right',
          background: pal.footerBg, borderRadius: '0 0 12px 12px',
          position: 'sticky', bottom: 0,
        }}>
          <button
            style={{
              padding: '8px 20px', background: pal.btnBg, color: '#fff',
              border: 0, borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 600,
            }}
            onClick={onClose}
            data-testid="omok-rules-close"
          >확인</button>
        </footer>
      </div>
    </div>
    </PalContext.Provider>
  );
}

const navLinkStyle: React.CSSProperties = { color: '#065f46', textDecoration: 'none' };
const bulletListStyle: React.CSSProperties = { margin: '0 0 12px', paddingLeft: 20 };

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  const pal = React.useContext(PalContext);
  return (
    <section id={id} style={{ marginBottom: 24, scrollMarginTop: 60 }}>
      <h3 style={{
        margin: '0 0 10px', fontSize: 16, fontWeight: 800, color: pal.accent,
        paddingBottom: 6, borderBottom: `2px solid ${pal.sectionLine}`,
      }}>
        {title}
      </h3>
      {children}
    </section>
  );
}

// V70G6E — 미니 보드 샘플 SVG (15x15 격자 축소 표현)
function MiniBoardSample({ caption }: { caption: string }) {
  const pal = React.useContext(PalContext);
  const size = 180;
  const PAD = 10;
  const inner = size - PAD * 2;
  const step = inner / 14;
  return (
    <figure style={{ margin: '12px 0', textAlign: 'center' }}>
      <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} style={{ background: '#f3e3c0', borderRadius: 6 }}>
        {Array.from({ length: 15 }, (_, i) => (
          <g key={i}>
            <line x1={PAD + i * step} y1={PAD} x2={PAD + i * step} y2={PAD + inner} stroke="#5a3f1a" strokeWidth={0.4} />
            <line x1={PAD} y1={PAD + i * step} x2={PAD + inner} y2={PAD + i * step} stroke="#5a3f1a" strokeWidth={0.4} />
          </g>
        ))}
        {[[7, 7], [3, 3], [3, 11], [11, 3], [11, 11]].map(([x, y]) => (
          <circle key={`${x}-${y}`} cx={PAD + x * step} cy={PAD + y * step} r={1.5} fill="#5a3f1a" />
        ))}
      </svg>
      <figcaption style={{ fontSize: 11, color: pal.muted, marginTop: 4 }}>{caption}</figcaption>
    </figure>
  );
}

// V70G6E — 금수 예시 미니 보드 세트 (33, 44, 장목)
function ForbidExamples() {
  // 각 예시: 돌 배치 + 금수 위치 (중심)
  const examples: Array<{ title: string; stones: Array<[number, number, 'b' | 'w']>; forbid: [number, number] }> = [
    {
      title: '33 (삼삼)',
      stones: [[3, 7, 'b'], [4, 7, 'b'], [5, 5, 'b'], [5, 6, 'b']],
      forbid: [5, 7],
    },
    {
      title: '44 (사사)',
      stones: [[4, 7, 'b'], [5, 7, 'b'], [6, 7, 'b'], [7, 4, 'b'], [7, 5, 'b'], [7, 6, 'b']],
      forbid: [7, 7],
    },
    {
      title: '장목 (6목)',
      stones: [[0, 7, 'b'], [1, 7, 'b'], [2, 7, 'b'], [3, 7, 'b'], [5, 7, 'b']],
      forbid: [4, 7],
    },
  ];
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
      gap: 10, marginTop: 10,
    }}>
      {examples.map((ex, i) => (
        <ExampleBoard key={i} {...ex} />
      ))}
    </div>
  );
}

function ExampleBoard({ title, stones, forbid }: {
  title: string;
  stones: Array<[number, number, 'b' | 'w']>;
  forbid: [number, number];
}) {
  const pal = React.useContext(PalContext);
  const size = 140;
  const PAD = 8;
  const inner = size - PAD * 2;
  const step = inner / 14;
  return (
    <div style={{ textAlign: 'center', background: pal.exBg, border: `1px solid ${pal.exBorder}`, borderRadius: 6, padding: 8 }}>
      <div style={{ fontSize: 11, color: pal.exTitle, fontWeight: 700, marginBottom: 4 }}>{title} 금수</div>
      <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} style={{ background: '#f3e3c0', borderRadius: 4 }}>
        {Array.from({ length: 15 }, (_, i) => (
          <g key={i}>
            <line x1={PAD + i * step} y1={PAD} x2={PAD + i * step} y2={PAD + inner} stroke="#5a3f1a" strokeWidth={0.3} />
            <line x1={PAD} y1={PAD + i * step} x2={PAD + inner} y2={PAD + i * step} stroke="#5a3f1a" strokeWidth={0.3} />
          </g>
        ))}
        {stones.map(([sx, sy, c], idx) => (
          <circle key={idx}
            cx={PAD + sx * step} cy={PAD + sy * step} r={step * 0.45}
            fill={c === 'b' ? '#1a1a1a' : '#f5f5f5'}
            stroke={c === 'b' ? '#000' : '#888'} strokeWidth={0.4}
          />
        ))}
        {/* 금수 X 표시 */}
        <g transform={`translate(${PAD + forbid[0] * step}, ${PAD + forbid[1] * step})`}>
          <circle r={step * 0.5} fill="rgba(239, 68, 68, 0.2)" />
          <line x1={-step * 0.3} y1={-step * 0.3} x2={step * 0.3} y2={step * 0.3} stroke="#dc2626" strokeWidth={1.5} />
          <line x1={-step * 0.3} y1={step * 0.3} x2={step * 0.3} y2={-step * 0.3} stroke="#dc2626" strokeWidth={1.5} />
        </g>
      </svg>
    </div>
  );
}

export default OmokRulesModal;
